import { useState } from 'react'
import { saveSession, type WorkoutToday } from '../lib/api'
import { Button, ErrorBanner, Group, Row, Screen } from '../components/ui'

/**
 * День без зала: бег, интервалы, прогулка, отдых.
 *
 * Раньше раздел назывался «Зал» и показывал тренажёры все семь дней подряд,
 * хотя зал в плане три раза в неделю. В остальные четыре человек открывал
 * чужой экран, а его пробежка не показывалась нигде.
 */

const SOURCE_LABELS: Record<string, string> = {
  manual: 'отметил сам',
  watch: 'записали часы',
  steps: 'по шагам за день',
}

function focusNote(details: Record<string, unknown>): string | null {
  const parts = [
    typeof details.km === 'number' ? `${details.km} км` : null,
    typeof details.minutes === 'number' ? `${details.minutes} минут` : null,
    typeof details.note === 'string' ? details.note : null,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

export function ActivityDay({
  data,
  onChanged,
  onOpenGym,
}: {
  data: WorkoutToday
  onChanged: () => void
  onOpenGym: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const own = data.activities.find((item) => item.kind === data.focus)
  const others = data.activities.filter((item) => item.kind !== data.focus)
  const note = focusNote(data.focus_details)

  async function mark(completed: boolean) {
    setBusy(true)
    setError(null)
    try {
      await saveSession({ kind: data.focus, completed, skipped: !completed })
      onChanged()
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const label = data.activities.find((item) => item.kind === data.focus)?.label ?? FOCUS_LABELS[data.focus] ?? 'Сегодня'

  return (
    <Screen title={label}>
      <div className="-mt-4">
        <div className="numerals flex items-baseline gap-2">
          <span className="text-[34px] font-semibold leading-none">
            {own ? (own.status === 'done' ? 'Сделано' : 'Пропущено') : 'Не отмечено'}
          </span>
        </div>
        {own?.detail && <p className="footnote mt-2">{own.detail} · {SOURCE_LABELS[own.source]}</p>}
        {!own && note && <p className="footnote mt-2">{note}</p>}
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {data.focus !== 'rest' && !own && (
        <div className="flex gap-2">
          <Button variant="filled" full onClick={() => mark(true)} disabled={busy}>
            Сделал
          </Button>
          <Button full onClick={() => mark(false)} disabled={busy} color="var(--color-ink-2)">
            Не сегодня
          </Button>
        </div>
      )}

      {others.length > 0 && (
        <Group
          caption="Тоже сегодня"
          footnote="Часы засчитывают пробежку и прогулку сами — отмечать руками не нужно."
        >
          {others.map((item) => (
            <Row
              key={`${item.kind}-${item.source}`}
              title={item.label}
              subtitle={SOURCE_LABELS[item.source]}
              value={item.detail || (item.status === 'skipped' ? 'пропущено' : 'сделано')}
            />
          ))}
        </Group>
      )}

      {data.activities.length === 0 && data.focus === 'rest' && (
        <Group caption="Отдых">
          <div className="footnote px-4 py-3.5">
            По плану сегодня выходной. Если всё-таки позанимался — отметь на главном экране.
          </div>
        </Group>
      )}

      <Button variant="plain" full onClick={onOpenGym}>
        Всё равно в зал →
      </Button>
    </Screen>
  )
}

const FOCUS_LABELS: Record<string, string> = {
  run: 'Бег 5 км',
  intervals: 'Интервалы 20 минут',
  walk: 'Прогулка',
  rest: 'Отдых',
}
